import { ViewIds } from '@ardoq/api-types';
import type { OnViewSettingsUpdate } from '@ardoq/data-model';
import {
  DropdownSliderOption,
  DropdownSliderOptionConfig,
} from '@ardoq/dropdown-menu';
import { debounce } from 'lodash';

type SubstituteMax = (value: number, max: number) => number;

const defaultSubstituter: SubstituteMax = value => value;

const getSubstituteMax: (substitute: number) => SubstituteMax =
  substitute => (value: number, max: number) =>
    value === max ? substitute : value;

const getSliderOnInput =
  (
    viewId: ViewIds,
    name: string,
    substituteMax: SubstituteMax = value => value,
    onViewSettingsUpdate: OnViewSettingsUpdate,
    max: number,
    settingsPath?: string[],
    persistent = true
  ) =>
  (value: number) => {
    const newValue = substituteMax(value, max);
    const settings = { [name]: newValue };

    onViewSettingsUpdate(viewId, settings, persistent, settingsPath);
  };

type OnValueChange = (value: number) => void;

export const getSettingsKeyWithOptionalPath = (
  key: string,
  settingsPath?: string[]
) => (settingsPath?.length ? `${settingsPath.join('-')}-${key}` : key);

const getSliderOnInputHandler = (
  viewId: ViewIds,
  cache: Map<string, (value: number) => void>,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  sliderConfig: DropdownSliderOptionConfig,
  persistent?: boolean
): OnValueChange => {
  const { name, substituteMax, settingsPath, max } = sliderConfig;
  const key = getSettingsKeyWithOptionalPath(name, settingsPath);

  if (!cache.has(key)) {
    const substituter =
      substituteMax === undefined
        ? defaultSubstituter
        : getSubstituteMax(substituteMax);
    cache.set(
      key,
      getSliderOnInput(
        viewId,
        name,
        substituter,
        onViewSettingsUpdate,
        max,
        settingsPath,
        persistent
      )
    );
  }

  return cache.get(key)!;
};

export const createDropdownSliderOptionManager =
  (
    viewId: ViewIds,
    cache: Map<string, OnValueChange>,
    onViewSettingsUpdate: OnViewSettingsUpdate,
    persistent?: boolean
  ) =>
  (sliderConfig: DropdownSliderOptionConfig): DropdownSliderOption => {
    const onValueChange = debounce(
      getSliderOnInputHandler(
        viewId,
        cache,
        onViewSettingsUpdate,
        sliderConfig,
        persistent
      ),
      150
    );

    return {
      ...sliderConfig,
      onValueChange:
        sliderConfig.value !== undefined ? onValueChange : undefined,
      onValueChangeEnd:
        sliderConfig.defaultValue !== undefined ? onValueChange : undefined,
    };
  };
